<template>
  <div v-if="loading" class="flex items-center justify-center h-full">
    <loading-spinner diameter="64px"/>
  </div>

  <alert-badge
      v-else-if="error.title"
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
      () => {
        error.title = ''
        error.body = ''
      }
    "
  />

  <div v-else class="flex flex-row">
    <div class="flex flex-col">
      <action-button :title="t('print')" faIcon="print" @click="print"/>
      <action-button :title="t('saveCard')" faIcon="download" @click="saveImage"/>
      <action-button
          v-if="$can('create', '/examination/zeiss-cards')"
          :title="$t('views.examinations.zeissCardsPage.newCard')"
          isSuccess
          @click="$router.push($Route.EXAMINATIONS_ZEISS_CARDS_NEW_CARD)"
      >
        <img class="new-card-icon" :src="newCardIcon" alt=""/>
      </action-button>
    </div>

    <div class="zeiss-card-preview mx-4 mt-3" dir="ltr">
      <div class="zeiss-card-scale">
      <div id="zeiss-card-print" class="zeiss-card">
        <div class="zc-header">
          <div class="zc-customer">
            <div class="zc-fit">Customer Name: <span class="zc-value">{{ card.customerName }}</span></div>
            <div class="zc-fit">Order Number: <span class="zc-value">{{ card.orderNumber }}</span></div>
          </div>
          <div class="zc-brand">
            <img class="zc-logo" :src="zeissLogo" alt="ZEISS"/>
            <div class="zc-date zc-fit">Date: {{ createdAt }}</div>
          </div>
        </div>

        <div class="zc-table">
          <div></div>
          <div class="zc-th">SPH</div>
          <div class="zc-th">CYL</div>
          <div class="zc-th">AXIS</div>
          <div class="zc-th">ADD</div>

          <div class="zc-eye">R</div>
          <div class="zc-td">{{ fmt(card.odSPH, 2) }}</div>
          <div class="zc-td">{{ fmt(card.odCYL, 2) }}</div>
          <div class="zc-td">{{ fmt(card.odAXIS, 0) }}</div>
          <div class="zc-td">{{ fmt(card.odADD, 2) }}</div>

          <div class="zc-eye">L</div>
          <div class="zc-td">{{ fmt(card.osSPH, 2) }}</div>
          <div class="zc-td">{{ fmt(card.osCYL, 2) }}</div>
          <div class="zc-td">{{ fmt(card.osAXIS, 0) }}</div>
          <div class="zc-td">{{ fmt(card.osADD, 2) }}</div>
        </div>

        <div class="zc-lens zc-fit">{{ card.lensType }}</div>
        <div class="zc-practitioner zc-fit">
          Authorized Eyecare Practitioner: <span class="zc-practitioner-name">{{ card.practitioner }}</span>
        </div>

        <div class="zc-terms-title">Terms &amp; Conditions</div>
        <div class="zc-terms">
          ZEISS lenses are warranted for 12 months against any manufacturing defects as specified
          by Carl Zeiss Vision manufacturing quality control standards. This does not cover damages
          caused by improper use or maintenance, such as, but not limited to excessive exposure to
          harmful chemical, heat and pressure, mechanical abuses, and damages incurred during
          fitting and dispensing.
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue'
import {useI18n} from 'vue-i18n'
import html2canvas from 'html2canvas'
import {formatPostgresDate} from '@/utils/date'

import ZeissCard, {zeissCardI18nMessages} from '@/models/customer/ZeissCard'
import zeissLogo from '@/assets/images/zeissLogoDataUri'
import newCardIcon from '@/assets/images/zeiss-new-card.svg'

import LoadingSpinner from '@/components/LoadingSpinner.vue'
import AlertBadge from '@/components/AlertBadge.vue'
import ActionButton from '@/components/ActionButton.vue'

function cardFromHistoryState(): ZeissCard | null {
  const state = window.history.state as {card?: Record<string, unknown>} | null
  return state?.card ? ZeissCard.from(state.card) : null
}

// Page-level print rules can't be scoped (and every view's CSS ships in one bundle),
// so they are injected only while this page is mounted. Otherwise they clip other prints.
const PRINT_CSS_ID = 'zeiss-card-print-css'
const PRINT_CSS = `
@media print {
  @page {
    size: 85.6mm 53.98mm;
    margin: 0;
  }
  html, body, #app {
    width: 85.6mm;
    height: 53.98mm;
    max-height: 53.98mm;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
  body * {
    visibility: hidden;
  }
}`

export default defineComponent({
  name: 'zeiss-card-print',

  components: {
    LoadingSpinner,
    AlertBadge,
    ActionButton
  },

  setup() {
    const {t} = useI18n({
      messages: {
        en: {...zeissCardI18nMessages.en},
        ar: {...zeissCardI18nMessages.ar}
      }
    })

    return {t, zeissLogo, newCardIcon}
  },

  data() {
    const initial = cardFromHistoryState()
    return {
      loading: initial === null,
      card: initial ?? new ZeissCard(),
      error: {title: '', body: ''}
    }
  },

  computed: {
    createdAt(): string {
      return this.card.createdAt
          ? formatPostgresDate(String(this.card.createdAt), 'DD/MM/YYYY')
          : ''
    }
  },

  async mounted() {
    this.addPrintCSS()
    if (!this.loading) {
      this.fitLines()
    }
    try {
      const resp = await this.$http.get<ZeissCard>(`${ZeissCard.ENDPOINT}/${this.$route.params.card_id}`)
      this.card = ZeissCard.from(resp)
    } catch (err) {
      const error = err as {title: string; body: string}
      this.error.title = error.title
      this.error.body = error.body
    } finally {
      this.loading = false
      await this.$nextTick()
      this.fitLines()
    }
  },

  beforeUnmount() {
    document.getElementById(PRINT_CSS_ID)?.remove()
  },

  methods: {
    addPrintCSS() {
      if (document.getElementById(PRINT_CSS_ID)) return
      const style = document.createElement('style')
      style.id = PRINT_CSS_ID
      style.textContent = PRINT_CSS
      document.head.appendChild(style)
    },

    fmt(value: number | undefined, precision: number) {
      if (value === undefined || value === null) return 'N/A'
      const fixed = Math.abs(value).toFixed(precision)
      if (precision === 0) return fixed
      if (value > 0) return `+ ${fixed}`
      if (value < 0) return `- ${fixed}`
      return fixed
    },

    fitLines() {
      const card = document.getElementById('zeiss-card-print')
      if (!card) return
      card.querySelectorAll<HTMLElement>('.zc-fit').forEach(el => {
        el.style.fontSize = ''
        let size = parseFloat(getComputedStyle(el).fontSize)
        while (el.scrollWidth > el.clientWidth && size > 3) {
          size -= 0.25
          el.style.fontSize = `${size}px`
        }
      })
    },

    print() {
      window.print()
    },

    // Rendered from an unscaled off-screen clone so the JPG keeps the card's real proportions.
    async saveImage() {
      const card = document.getElementById('zeiss-card-print')
      if (!card) return
      const holder = document.createElement('div')
      holder.style.cssText = 'position:fixed;left:-10000px;top:0;'
      const clone = card.cloneNode(true) as HTMLElement
      clone.removeAttribute('id')
      clone.style.borderRadius = '0'
      holder.appendChild(clone)
      document.body.appendChild(holder)
      try {
        const canvas = await html2canvas(clone, {scale: 4, backgroundColor: '#ffffff'})
        const link = document.createElement('a')
        link.href = canvas.toDataURL('image/jpeg', 0.95)
        const name = [this.card.customerName, this.card.orderNumber].filter(Boolean).join('-')
        link.download = `ZEISS-${name || this.card.externalID || 'card'}.jpg`
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (err) {
        this.error.title = 'Error saving image'
        this.error.body = (err as Error).message || 'An unknown error occurred'
      } finally {
        holder.remove()
      }
    }
  }
})
</script>

<style scoped>
/* CR80 card: 85.6mm x 53.98mm. Everything inside is sized in mm so the print is exact. */
.zeiss-card {
  width: 85.6mm;
  height: 53.98mm;
  padding: 2.4mm 2.6mm 2.4mm 2.6mm;
  box-sizing: border-box;
  background: #fff;
  color: #000;
  direction: ltr;
  font-family: Verdana, Tahoma, 'DejaVu Sans', Arial, sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 2.5mm;
}

.zeiss-card > * {
  flex-shrink: 0;
}

.zeiss-card-preview {
  width: calc(85.6mm * 2.2);
  height: calc(53.98mm * 2.2);
  flex-shrink: 0;
}

.zeiss-card-scale {
  width: 85.6mm;
  height: 53.98mm;
  transform: scale(2.2);
  transform-origin: top left;
}

.new-card-icon {
  height: 3.5rem;
  margin: 0 auto;
}

.zeiss-card-preview .zeiss-card {
  box-shadow: 0 1mm 3mm rgba(0, 0, 0, 0.25);
}

.zc-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.zc-customer {
  font-size: 2.9mm;
  font-weight: 700;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
  margin-right: 1.5mm;
}

.zc-fit {
  white-space: nowrap;
  overflow: hidden;
  display: block;
}

.zc-value {
  font-weight: 700;
}

.zc-brand {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.zc-logo {
  width: 7.5mm;
  height: 7.5mm;
  display: block;
  margin-right: 0.8mm;
}

.zc-date {
  font-size: 2.4mm;
  font-weight: 700;
  margin-top: 1mm;
}

.zc-table {
  display: grid;
  grid-template-columns: 15% 20% 18% 17% 15%;
  align-items: baseline;
  margin-top: 1.6mm;
  row-gap: 0.3mm;
}

.zc-th {
  text-align: center;
  font-size: 2.4mm;
  font-weight: 700;
}

.zc-eye {
  font-size: 2.8mm;
  font-weight: 700;
}

.zc-td {
  text-align: center;
  font-size: 3.2mm;
  font-weight: 700;
  white-space: nowrap;
}

.zc-lens {
  font-size: 2.3mm;
  font-weight: 700;
  margin-top: 1.2mm;
}

.zc-practitioner {
  font-size: 2.3mm;
  font-weight: 700;
  margin-top: 0.3mm;
  line-height: 1.2;
}

.zc-practitioner-name {
  color: #283EE0;
  font-weight: 700;
}

.zc-terms-title {
  font-size: 2.1mm;
  font-weight: 700;
  margin-top: 0.6mm;
}

.zc-terms {
  font-size: 1.95mm;
  line-height: 1.15;
  margin-top: 0.4mm;
  text-align: justify;
}

@media print {
  .zeiss-card-preview,
  .zeiss-card-scale {
    transform: none;
    width: 85.6mm;
    height: 53.98mm;
    margin: 0;
  }

  #zeiss-card-print,
  #zeiss-card-print * {
    visibility: visible;
  }

  #zeiss-card-print {
    position: fixed;
    left: 0;
    top: 0;
    margin: 0;
    box-shadow: none;
    border-radius: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
